// pages/GlobalBookPage.tsx
// 「この1冊」個別ページ（/globalbook/:globalBookId）
// 本自体・本棚のどちらかが非公開なら通常の書誌ページへリダイレクトする

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MuiLink from '@mui/material/Link';

import { NEW_API, buildBibSimpleUrl } from '../config/api';
import { BibSimpleResponse } from '../types/rlTypes';
import { getBibTitle, getBibIsbn, getBibPublisher, getBibPublishingDate, generateCoverUrlFromBib } from '../utils/bibAdapter';
import { getUsernameFromUsersubCF } from '../utils/userProfileApi';
import { useAppTokens } from '../context/ThemeModeContext';

interface GlobalBookPublicData {
  found: boolean;
  is_public?: boolean;
  rl_book_id?: number;
  user_sub?: string;
  shelf_id?: string;
  shelf_name?: string;
  registered_at?: string;
}

const GlobalBookPage: React.FC = () => {
  const { globalBookId } = useParams<{ globalBookId: string }>();
  const navigate = useNavigate();
  const TOKENS = useAppTokens();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [bibData, setBibData] = useState<BibSimpleResponse | null>(null);
  const [shelfName, setShelfName] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!globalBookId) return;
      setLoading(true);
      try {
        const res = await axios.get(NEW_API.BOOKS, {
          params: { public_global_book_id: globalBookId },
        });
        const data = res.data?.data as GlobalBookPublicData;

        if (!data?.found) {
          setNotFound(true);
          return;
        }

        if (!data.is_public || !data.rl_book_id) {
          // 本または本棚が非公開 → 通常の書誌ページへリダイレクト
          if (data.rl_book_id) {
            navigate(`/book/${data.rl_book_id}`, { replace: true });
          } else {
            setNotFound(true);
          }
          return;
        }

        setShelfName(data.shelf_name ?? null);

        const [bibRes, usernameRes] = await Promise.all([
          axios.get(buildBibSimpleUrl(data.rl_book_id)),
          data.user_sub ? getUsernameFromUsersubCF(data.user_sub) : Promise.resolve(null),
        ]);
        setBibData(bibRes.data as BibSimpleResponse);
        setUsername(usernameRes);
      } catch (error) {
        console.error('Error fetching global book:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [globalBookId, navigate]);

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    backdropFilter: 'blur(6px)',
  } as const;

  // ページ全体の背景（GraphPage / AuthorsDetailPage 等と同じ、TOKENS.bg ベースのグラデーション）。
  // これを敷かずに Container だけ置くと、MUIのデフォルト背景（白系）の上にカードだけ
  // クリーム色で浮く「昔の配色」に見えてしまうため、必ずこのラッパーで囲う
  const PageBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100dvh - 56px)',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
        }}
      >
        {children}
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <PageBackground>
        <Container maxWidth="sm" sx={{ pt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: TOKENS.text }} />
        </Container>
      </PageBackground>
    );
  }

  if (notFound || !bibData) {
    return (
      <PageBackground>
        <Container maxWidth="sm" sx={{ pt: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, color: TOKENS.textSub }}>
            この本は見つかりませんでした。
          </Typography>
        </Container>
      </PageBackground>
    );
  }

  const title = getBibTitle(bibData);
  const isbn = getBibIsbn(bibData);
  const publisher = getBibPublisher(bibData);
  const publishingDate = getBibPublishingDate(bibData);

  return (
    <PageBackground>
      <Container maxWidth="sm" sx={{ pt: 8, pb: 6 }}>
        <Box sx={{ ...glassPanelSx, p: 3 }}>
          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 2 }}>
            {username ? (
              <>
                <MuiLink
                  component={Link}
                  to={`/${username}`}
                  sx={{ color: TOKENS.text, fontWeight: 600 }}
                >
                  {username}
                </MuiLink>
                {' さんの蔵書'}
              </>
            ) : (
              'ユーザーの蔵書'
            )}
            {shelfName && ` — ${shelfName}`}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Card sx={{ width: 110, flexShrink: 0, boxShadow: 'none', background: TOKENS.cardBg, border: `1px solid ${TOKENS.border}` }}>
              <CardMedia
                component="img"
                image={generateCoverUrlFromBib(bibData)}
                alt={title}
                sx={{ objectFit: 'contain', height: 150, width: '100%', padding: '6px' }}
              />
            </Card>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: TOKENS.text, lineHeight: 1.3 }}>
                {title}
              </Typography>
              {publisher && (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 1 }}>
                  {publisher}
                </Typography>
              )}
              {publishingDate && (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  {publishingDate}
                </Typography>
              )}
              {isbn && (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  ISBN {isbn}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              component={Link}
              to={`/book/${bibData.rl_book_id}`}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none', color: TOKENS.text, borderColor: TOKENS.borderSoft }}
            >
              この本の書誌ページを見る
            </Button>
          </Box>
        </Box>
      </Container>
    </PageBackground>
  );
};

export default GlobalBookPage;
